/**
 * Strings for the oral exam (experimental): Settings → Oral exam
 * (components/settings/VoiceSection.tsx), the voice controls of the exam
 * session (components/exam/VoiceControls.tsx) and its spoken turn
 * (components/exam/SpokenAnswer.tsx).
 *
 * Spread into the main catalog; `VOICE_EN` is typed against `VOICE_ES`.
 */

export const VOICE_ES = {
  // ── Ajustes → Evaluación oral ─────────────────────────────────────────────
  'settings.voice.title': 'Evaluación oral',
  'settings.voice.description': 'Responde hablando y escucha las preguntas. El reconocimiento y la síntesis de voz se ejecutan en este navegador con WebAssembly: tu voz nunca sale del dispositivo. Solo el texto transcrito va a tu modelo, igual que una respuesta escrita.',
  'settings.voice.enabled': 'Activar la evaluación oral',
  'settings.voice.enabledHint': 'Ofrece el examen oral y añade un micrófono a las respuestas. No descarga nada hasta que empiezas un examen oral, y antes te dice cuánto ocupa. Con los modelos en el dispositivo, el oral pasa a ser la opción principal del examen; por escrito sigue a un toque.',
  'settings.voice.language': 'Idioma de la voz',
  'settings.voice.languageHint': 'El idioma en que respondes y en que se leen las preguntas.',
  'settings.voice.language.auto': 'Como la app',
  'settings.voice.language.es': 'Español',
  'settings.voice.language.en': 'English',
  'settings.voice.language.pt': 'Português',
  'settings.voice.language.fr': 'Français',
  'settings.voice.stt': 'Reconocimiento de voz',
  'settings.voice.sttHint': 'Whisper, ejecutado en este dispositivo. Se descarga una vez y después funciona sin conexión.',
  'settings.voice.stt.whisper-tiny': 'Whisper tiny · {size} MB · rápido',
  'settings.voice.stt.whisper-base': 'Whisper base · {size} MB · recomendado',
  'settings.voice.stt.whisper-small': 'Whisper small · {size} MB · más preciso, lento',
  'settings.voice.tts': 'Voz que lee',
  'settings.voice.ttsHint.system': 'Las voces de tu sistema operativo. Sin descarga; en algunos sistemas la voz puede procesarse en la nube.',
  'settings.voice.ttsHint.supertonic': 'Supertonic 2 en este dispositivo: voz natural, sin conexión una vez descargada. Descarga grande: mejor con wifi.',
  'settings.voice.ttsHint.off': 'Las preguntas no se leen en voz alta.',
  'settings.voice.tts.off': 'Desactivada',
  'settings.voice.tts.system': 'Voz del sistema',
  'settings.voice.tts.supertonic': 'Supertonic 2 · {size} MB',
  'settings.voice.voice': 'Voz',
  'settings.voice.voice.F': 'Femenina {n}',
  'settings.voice.voice.M': 'Masculina {n}',
  'settings.voice.rate': 'Velocidad',
  'settings.voice.autoRead': 'Leer cada pregunta al aparecer',
  'settings.voice.autoReadHint': 'En el examen oral. Siempre puedes repetirla con el altavoz.',
  'settings.voice.try': 'Probar',
  'settings.voice.sample': 'Esta es la voz que leerá las preguntas del examen.',
  'settings.voice.models': 'Modelos en este dispositivo',
  'settings.voice.modelsNone': 'Ninguno descargado todavía.',
  'settings.voice.modelsDelete': 'Borrar modelos',
  'settings.voice.modelsDeleted': 'Modelos borrados de este dispositivo.',
  'settings.voice.download': 'Descargar ahora',
  'settings.voice.source': 'Los modelos se descargan de Hugging Face (huggingface.co) sin enviar nada tuyo. Licencias: Whisper (MIT), Silero VAD (MIT, 2 MB, detecta cuándo hablas) y Supertonic 2 (OpenRAIL).',

  // ── Estado de los modelos ─────────────────────────────────────────────────
  'voice.status.idle': 'Se descargará al usarlo por primera vez.',
  'voice.status.cached': 'Descargado ({size}).',
  'voice.status.loading': 'Preparando el modelo…',
  'voice.status.downloading': 'Descargando… {percent} %',
  'voice.status.ready': 'Listo.',
  'voice.error.microphone': 'No se pudo usar el micrófono. Revisa el permiso del navegador para esta página.',
  'voice.error.network': 'No se pudo descargar el modelo. Comprueba la conexión y vuelve a intentarlo.',
  'voice.error.memory': 'Este dispositivo se quedó sin memoria para el modelo. Prueba uno más pequeño.',
  'voice.error.unsupported': 'Este navegador no puede ejecutar el modelo (WebAssembly o grabación no disponibles).',
  'voice.error.other': 'Falló la voz: {message}',

  // ── Examen ────────────────────────────────────────────────────────────────
  'voice.listen': 'Escuchar',
  'voice.stopListening': 'Detener lectura',
  'voice.record': 'Responder hablando',
  'voice.firstDownload': 'La primera vez descarga unos {mb} MB.',
  'voice.recordMore': 'Grabar más',
  'voice.stop': 'Detener y transcribir',
  'voice.cancel': 'Descartar',
  'voice.recording': 'Grabando · {time}',
  'voice.transcribing': 'Transcribiendo en este dispositivo…',
  'voice.preparing': 'Preparando el reconocimiento de voz…',
  'voice.heardNothing': 'No se oyó nada.',
  'voice.editHint': 'Revisa la transcripción antes de continuar: puedes corregirla.',
  'voice.code': '(código)',
  'voice.autoStopped': 'La grabación se detuvo a los 3 minutos.',

  // ── Turno de voz del examen oral ──────────────────────────────────────────
  'settings.voice.endSilence': 'Silencio que cierra tu turno',
  'settings.voice.endSilenceHint': 'Las pausas más cortas no cortan tu respuesta: son para pensar. También puedes decir «listo» o tocar «He terminado».',
  'settings.voice.startSilence': 'Espera para empezar',
  'settings.voice.startSilenceHint': 'Desde que termina la lectura hasta tus primeras palabras.',
  'settings.voice.modeNote': 'El modo (Práctica, Examen o Estricto) se elige al empezar cada examen: Práctica espera más y ofrece ayuda; Examen y Estricto dan la pregunta por no respondida.',
  'settings.voice.byMode': 'Según el modo · {practice} en Práctica, {exam} en Examen',
  'settings.voice.seconds': '{value} s',
  'voice.turn.preparing': 'Preparando el micrófono…',
  'voice.turn.readingPrep': 'Preparando la lectura…',
  'voice.turn.reading': 'Escucha la pregunta',
  'voice.turn.readingSilent': 'Lee la pregunta',
  'voice.turn.countdown': 'Tu turno en {n}',
  'voice.turn.listening': 'Te escucho',
  'voice.turn.listeningHint': 'Las pausas cortas no cortan tu respuesta. Al acabar, di «listo» o calla un momento. Si no lo sabes, di «no sé».',
  'voice.turn.heard': 'Lo que oigo',
  'voice.turn.finish': 'He terminado',
  'voice.turn.repeat': 'Repetir la pregunta',
  'voice.turn.write': 'Escribir',
  'voice.turn.speak': 'Responder hablando',
  'voice.turn.finishing': 'Transcribiendo lo último…',
  'voice.turn.transcribing': 'Transcribiendo…',
  'voice.turn.transcribingHint': 'Ya no te escucho: estoy pasando a texto lo que dijiste.',
  'voice.turn.noSpeech.title': 'No te oigo',
  'voice.turn.noSpeech.body': '¿Te repito la pregunta o prefieres una pista? Sigo escuchando.',
  'voice.turn.cap.title': 'Llevas {s} s sin hablar',
  'voice.turn.cap.body': '¿Lo dejamos para luego? Puedes pausar el examen y seguir cuando puedas, o salir. Lo que ya respondiste está guardado.',
  'voice.turn.cap.stay': 'Seguir aquí',
  'settings.voice.silenceCap': 'Silencio máximo antes de hablar',
  'settings.voice.silenceCapHint': 'Si pasa este tiempo sin que digas nada, sumando preguntas seguidas, el examen deja de escuchar y te propone pausarlo o salir.',
  'voice.turn.listenAgain': 'Volver a escuchar',
  'voice.turn.addMore': 'Añadir hablando',
  'voice.turn.retry': 'Volver a intentar',
  'voice.turn.unanswered': 'Sin respuesta: la pregunta pasa y queda como deuda.',
  'exam.timing.title': 'Tus tiempos',
  'exam.timing.start': 'Tardaste {s} s en empezar.',
  'exam.timing.speaking': 'Hablaste {s} s, a {wpm} palabras por minuto.',
  'exam.timing.speakingShort': 'Hablaste {s} s.',
  'exam.timing.pause': 'Te detuviste {s} s antes de «{words}».',
  'exam.timing.fillers': 'Muletillas: {count} ({list}).',
  'exam.timing.repeats': 'Pediste repetir la pregunta {count} veces.',
  'exam.timing.repeats.one': 'Pediste repetir la pregunta una vez.',
  'exam.timing.silent': 'Turnos en los que no se oyó nada: {count}.',
  'exam.timing.note': 'Sirve para ver dónde dudas. Por ahora solo se informa: cuando cuente para la nota, pesará menos que la precisión y nunca suspenderá por sí sola una respuesta correcta.',
  'exam.home.startOral': 'Examen oral',
  'exam.session.oral': 'Examen oral',
} as const

export const VOICE_EN: { [K in keyof typeof VOICE_ES]: string } = {
  'settings.voice.title': 'Oral exam',
  'settings.voice.description': 'Answer out loud and listen to the questions. Speech recognition and synthesis run in this browser with WebAssembly: your voice never leaves the device. Only the transcript goes to your model, like a written answer.',
  'settings.voice.enabled': 'Turn on the oral exam',
  'settings.voice.enabledHint': 'Offers the oral exam and adds a microphone to answers. Nothing downloads until you start an oral exam, and you are told how big it is first. Once the models are on the device, the oral exam becomes the exam’s main option; writing stays one tap away.',
  'settings.voice.language': 'Voice language',
  'settings.voice.languageHint': 'The language you answer in and the questions are read in.',
  'settings.voice.language.auto': 'Same as the app',
  'settings.voice.language.es': 'Español',
  'settings.voice.language.en': 'English',
  'settings.voice.language.pt': 'Português',
  'settings.voice.language.fr': 'Français',
  'settings.voice.stt': 'Speech recognition',
  'settings.voice.sttHint': 'Whisper, running on this device. Downloaded once, then it works offline.',
  'settings.voice.stt.whisper-tiny': 'Whisper tiny · {size} MB · fast',
  'settings.voice.stt.whisper-base': 'Whisper base · {size} MB · recommended',
  'settings.voice.stt.whisper-small': 'Whisper small · {size} MB · more accurate, slow',
  'settings.voice.tts': 'Reading voice',
  'settings.voice.ttsHint.system': 'Your operating system’s voices. No download; on some systems the voice may be processed in the cloud.',
  'settings.voice.ttsHint.supertonic': 'Supertonic 2 on this device: a natural voice that works offline once downloaded. Large download: better on wifi.',
  'settings.voice.ttsHint.off': 'Questions are not read aloud.',
  'settings.voice.tts.off': 'Off',
  'settings.voice.tts.system': 'System voice',
  'settings.voice.tts.supertonic': 'Supertonic 2 · {size} MB',
  'settings.voice.voice': 'Voice',
  'settings.voice.voice.F': 'Female {n}',
  'settings.voice.voice.M': 'Male {n}',
  'settings.voice.rate': 'Speed',
  'settings.voice.autoRead': 'Read each question when it appears',
  'settings.voice.autoReadHint': 'In the oral exam. You can always replay it with the speaker.',
  'settings.voice.try': 'Try it',
  'settings.voice.sample': 'This is the voice that will read the exam questions.',
  'settings.voice.models': 'Models on this device',
  'settings.voice.modelsNone': 'None downloaded yet.',
  'settings.voice.modelsDelete': 'Delete models',
  'settings.voice.modelsDeleted': 'Models deleted from this device.',
  'settings.voice.download': 'Download now',
  'settings.voice.source': 'Models are downloaded from Hugging Face (huggingface.co) without sending anything of yours. Licenses: Whisper (MIT), Silero VAD (MIT, 2 MB, detects when you speak) and Supertonic 2 (OpenRAIL).',

  'voice.status.idle': 'Downloaded the first time you use it.',
  'voice.status.cached': 'Downloaded ({size}).',
  'voice.status.loading': 'Preparing the model…',
  'voice.status.downloading': 'Downloading… {percent}%',
  'voice.status.ready': 'Ready.',
  'voice.error.microphone': 'The microphone could not be used. Check the browser permission for this page.',
  'voice.error.network': 'The model could not be downloaded. Check the connection and try again.',
  'voice.error.memory': 'This device ran out of memory for the model. Try a smaller one.',
  'voice.error.unsupported': 'This browser cannot run the model (WebAssembly or recording unavailable).',
  'voice.error.other': 'Voice failed: {message}',

  'voice.listen': 'Listen',
  'voice.stopListening': 'Stop reading',
  'voice.record': 'Answer out loud',
  'voice.firstDownload': 'The first time it downloads about {mb} MB.',
  'voice.recordMore': 'Record more',
  'voice.stop': 'Stop and transcribe',
  'voice.cancel': 'Discard',
  'voice.recording': 'Recording · {time}',
  'voice.transcribing': 'Transcribing on this device…',
  'voice.preparing': 'Preparing speech recognition…',
  'voice.heardNothing': 'Nothing was heard.',
  'voice.editHint': 'Check the transcript before you continue: you can correct it.',
  'voice.code': '(code)',
  'voice.autoStopped': 'Recording stopped at 3 minutes.',

  'settings.voice.endSilence': 'Silence that ends your turn',
  'settings.voice.endSilenceHint': 'Shorter pauses do not cut your answer: they are for thinking. You can also say “done” or tap “I’m done”.',
  'settings.voice.startSilence': 'Wait before you start',
  'settings.voice.startSilenceHint': 'From the end of the reading to your first words.',
  'settings.voice.modeNote': 'The mode (Practice, Exam or Strict) is chosen when each exam starts: Practice waits longer and offers help; Exam and Strict pass the question unanswered.',
  'settings.voice.byMode': 'As the mode · {practice} in Practice, {exam} in Exam',
  'settings.voice.seconds': '{value} s',
  'voice.turn.preparing': 'Preparing the microphone…',
  'voice.turn.readingPrep': 'Preparing the reading…',
  'voice.turn.reading': 'Listen to the question',
  'voice.turn.readingSilent': 'Read the question',
  'voice.turn.countdown': 'Your turn in {n}',
  'voice.turn.listening': 'I’m listening',
  'voice.turn.listeningHint': 'Short pauses do not cut your answer. When you finish, say “done” or stay quiet for a moment. If you do not know, say “I don’t know”.',
  'voice.turn.heard': 'What I hear',
  'voice.turn.finish': 'I’m done',
  'voice.turn.repeat': 'Repeat the question',
  'voice.turn.write': 'Type instead',
  'voice.turn.speak': 'Answer out loud',
  'voice.turn.finishing': 'Transcribing the last part…',
  'voice.turn.transcribing': 'Transcribing…',
  'voice.turn.transcribingHint': 'No longer listening: turning what you said into text.',
  'voice.turn.noSpeech.title': 'I can’t hear you',
  'voice.turn.noSpeech.body': 'Shall I repeat the question, or would you like a hint? I’m still listening.',
  'voice.turn.cap.title': 'You have been silent for {s} s',
  'voice.turn.cap.body': 'Shall we leave it for later? You can pause the exam and come back when you can, or leave. What you already answered is saved.',
  'voice.turn.cap.stay': 'Stay',
  'settings.voice.silenceCap': 'Longest silence before speaking',
  'settings.voice.silenceCapHint': 'If this long goes by without you saying anything, over questions in a row, the exam stops listening and offers to pause or leave.',
  'voice.turn.listenAgain': 'Listen again',
  'voice.turn.addMore': 'Add more out loud',
  'voice.turn.retry': 'Try again',
  'voice.turn.unanswered': 'No answer: the question passes and counts as debt.',
  'exam.timing.title': 'Your timing',
  'exam.timing.start': 'You took {s} s to start.',
  'exam.timing.speaking': 'You spoke for {s} s, at {wpm} words a minute.',
  'exam.timing.speakingShort': 'You spoke for {s} s.',
  'exam.timing.pause': 'You stopped for {s} s before “{words}”.',
  'exam.timing.fillers': 'Fillers: {count} ({list}).',
  'exam.timing.repeats': 'You asked to repeat the question {count} times.',
  'exam.timing.repeats.one': 'You asked to repeat the question once.',
  'exam.timing.silent': 'Turns where nothing was heard: {count}.',
  'exam.timing.note': 'It shows where you hesitate. For now it is only reported: when it counts for the grade, it will weigh less than precision and never fail a correct answer by itself.',
  'exam.home.startOral': 'Oral exam',
  'exam.session.oral': 'Oral exam',
}
